import { useState, useEffect } from 'react';
import { AlertTriangle, Loader2, PlusCircle, SearchX } from 'lucide-react';
import HeroSection from '@/components/ui-custom/HeroSection';
import RideMap from '@/components/map/RideMap';
import RideListView from '@/components/ui-custom/RideListView';
import RideSubmissionForm from '@/components/rides/RideSubmissionForm';
import AuthModal from '@/components/auth/AuthModal';
import RideHubSidebar from '@/components/rides/RideHubSidebar';
import RideHubHeader from '@/components/rides/RideHubHeader';
import RideCard from '@/components/ui-custom/RideCard';
import { useAuth } from '@/contexts/AuthContext';
//import { useNearby } from '@/hooks/useNearby';
import { useToast } from '@/hooks/use-toast';
import { useRideData } from '@/hooks/useRideData';
import { DifficultyFilter, SkillFilter, RideTypeFilter, TimeFilter, MultiDayFilter, ViewMode, Point } from '@/lib/types/core';
import { TerrainType } from '@/lib/types/core/terrain';
import { useGeolocationRideFiltering } from '@/components/rides/hooks/useGeolocationRideFiltering';

const RideHub = () => {
  const [difficultyFilter, setDifficultyFilter] = useState<DifficultyFilter>('All');
  const [skillFilter, setSkillFilter] = useState<SkillFilter>('All');
  const [rideTypeFilter, setRideTypeFilter] = useState<RideTypeFilter>('All');
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('Future');
  const [multiDayFilter, setMultiDayFilter] = useState<MultiDayFilter>('All');
  const [terrainFilter, setTerrainFilter] = useState<TerrainType[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>('map');
  const [showSubmissionForm, setShowSubmissionForm] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [nearbyPoint, setNearbyPoint] = useState<Point | null>(null);
  const { user, loading: userLoading } = useAuth();
  const { isLocating: nearbyLoading } = useGeolocationRideFiltering({ setNearbyPoint })
  const { toast } = useToast();

  const { rides, loading, mapError } = useRideData(
    difficultyFilter,
    skillFilter,
    rideTypeFilter,
    timeFilter,
    multiDayFilter,
    terrainFilter,
    nearbyPoint,
    setNearbyPoint,
    user,
    userLoading
  );

  const handleSubmitClick = () => {
    if (!user) {
      setShowAuthModal(true);
    } else {
      setShowSubmissionForm(true);
    }
  };

  const handleRideSubmissionSuccess = () => {
    toast({
      title: "Ride submitted successfully!",
      description: "Your ride has been added to the map and is now visible to the community."
    });
    setTimeout(() => {
      window.location.reload();
    }, 2000);
  };

  return (
    <div className="bg-adv-sand min-h-screen">
      <HeroSection
        title="Explore Rides"
        subtitle="Discover group rides and connect with local adventure riders."
        backgroundImage="/images/ridehub-hero.jpg"
      />
      
      <main id="content" className="py-20">
        <div className="container-custom">
          <div className="flex flex-col lg:flex-row gap-8">
            <RideHubSidebar
              timeFilter={timeFilter}
              setTimeFilter={setTimeFilter}
              difficultyFilter={difficultyFilter}
              setDifficultyFilter={setDifficultyFilter}
              setRideFilterLocation={setNearbyPoint}
              skillFilter={skillFilter}
              setSkillFilter={setSkillFilter}
              rideTypeFilter={rideTypeFilter}
              setRideTypeFilter={setRideTypeFilter}
              multiDayFilter={multiDayFilter}
              setMultiDayFilter={setMultiDayFilter}
              terrainFilter={terrainFilter}
              setTerrainFilter={setTerrainFilter}
              handleSubmitClick={handleSubmitClick}
              rides={rides}
            />
            
            <div className="lg:w-3/4">
              <div className="mb-8">
                <RideHubHeader viewMode={viewMode} setViewMode={setViewMode} />
                
                {mapError ? (
                  <div className="bg-adv-charcoal rounded-lg p-6 text-center">
                    <AlertTriangle size={32} className="text-adv-orange mb-4 mx-auto" />
                    <h3 className="text-lg font-bold text-white mb-2">Failed to Load Rides</h3>
                    <p className="text-adv-lightGray mb-4">{mapError}</p>
                    <button 
                      onClick={() => window.location.reload()} 
                      className="btn-primary"
                    >
                      Retry
                    </button>
                  </div>
                ) : (
                  viewMode === 'map' ? (
                    <RideMap
                    rides={rides}
                    loading={loading}
                    defaultCenter={nearbyPoint ? {
                      longitude: nearbyPoint.longitude,
                      latitude: nearbyPoint.latitude
                    } : {  // US center
                      longitude: -98.5795,
                      latitude: 39.8283,
                    }}
                    setPoint={setNearbyPoint}
                    showListView={false} />
                  ) : (
                    <RideListView rides={rides} loading={loading} />
                  )
                )}
              </div>
              
              {viewMode === 'map' && (
                <>
                  <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between">
                    <h2 className="heading-lg">Upcoming Rides</h2>
                    <div className="text-adv-lightGray mt-2 sm:mt-0">
                      Showing {rides.length} {rides.length === 1 ? 'ride' : 'rides'}
                    </div>
                  </div>
                  
                  {loading ? (
                    <div className="flex justify-center py-12">
                      <div className="text-center">
                        <Loader2 size={32} className="text-adv-orange animate-spin mb-4 mx-auto" />
                        <p className="text-white">Loading rides...</p>
                      </div>
                    </div>
                  ) : rides.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                      {rides.map((ride) => (
                        <RideCard
                          key={ride.id}
                          id={ride.id}
                          title={ride.title}
                          date={ride.meeting_date}
                          location={ride.meeting_address || `${ride.meeting_longitude}, ${ride.meeting_latitude}`}
                          difficulty={ride.terrain_difficulty}
                          distance={ride.ride_type}
                          organizer={ride.created_by}
                          multi_day={ride.multi_day}
                          end_date={ride.end_date}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="glass-card rounded-lg p-12 text-center">
                      <SearchX size={48} className="text-adv-lightGray mb-4 mx-auto" />
                      <h3 className="text-xl font-bold text-white mb-2">No rides found</h3>
                      <p className="text-adv-lightGray mb-6">
                        {rides.length > 0 
                          ? "Try adjusting your filters to see more rides." 
                          : "There are no upcoming rides scheduled."}
                      </p>
                      <button
                        onClick={handleSubmitClick}
                        className="btn-primary inline-flex items-center"
                      >
                        <PlusCircle size={18} className="mr-2" /> Create a Ride
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </main>
      
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)}
        onSignInSuccess={() => setShowAuthModal(false)}
        onSignUpSuccess={() => setShowAuthModal(false)}
      />
      
      <RideSubmissionForm
        isOpen={showSubmissionForm}
        onClose={() => setShowSubmissionForm(false)}
        onSubmitSuccess={handleRideSubmissionSuccess}
      />
    </div>
  );
};

export default RideHub;
